import { Suspense, useEffect } from "react";
import { isRouteErrorResponse, Link, useLocation, useRouteLoaderData } from "react-router";
import {
  NotFoundPage,
  ServerErrorPage,
  type ErrorPageDestination,
} from "@noiman/ui/error-pages";
import { capturePostHogEvent } from "@/lib/analytics/posthog.client";
import {
  errorPageSurface,
  LAB_ARTICLE_DESTINATIONS,
  notFoundDestinations,
  serverErrorDestinations,
  SURFACE_LEAD,
  type ErrorPageSurface,
} from "@/lib/not-found/destinations";
import { lazyNamed } from "@/lib/lazy-named";

/*
ヘッダーは後から読む。エラー画面は root に載るので、ここで静的に読むと
PageHeader がヘッダーを使わないページも含めた全ページの初回の読み込みに入る
*/
/* 場面の絵。描き直したら日付を変えた別の名前で置く (public/_headers で 1 年 immutable) */
/* 404 は、点の道の先に扉の形の点線の枠だけがある場面 (10/22 に描き直し。前の絵は黄が 3 か所あった) */
const NOT_FOUND_ART_SRC = "/empty-art/notfound-2026-10-24.svg";
const SERVER_ERROR_ART_SRC = "/empty-art/error-server-2026-10-24.svg";

const LearnErrorHeader = lazyNamed(
  () => import("./LearnErrorHeader"),
  (m) => m.LearnErrorHeader,
);
/* 受講生の 404。/luagate の部品 (lg.css ごと) を、受講生が 404 を開いたときだけ読む */
const StudentNotFound = lazyNamed(
  () => import("./StudentNotFound"),
  (m) => m.StudentNotFound,
);

const LabErrorHeader = lazyNamed(
  () => import("@/components/lab/LabShellHeader"),
  (m) => m.LabShellHeader,
);

/*
読み込むまでの間は、ヘッダーと同じ高さの白い帯で場所を取っておく。
学習側の高さは 767px 以下で 78.5px、1024px 以上で 76px。その間はナビの
折り返しで 92px から 132px まで動くので、中ほどの 92px を取る
*/
function HeaderPlaceholder({ surface }: { surface: ErrorPageSurface }) {
  return (
    <div
      aria-hidden
      data-error-page-header-placeholder={surface}
      className={
        surface === "lab"
          ? "h-[74px] border-b border-gray-light/50 bg-white"
          : "h-[78.5px] border-b border-main bg-white md:h-[92px] lg:h-[76px]"
      }
    />
  );
}

export function notFoundSuggestionsFrom(
  error: unknown,
): ErrorPageDestination[] {
  if (!isRouteErrorResponse(error) || error.status !== 404) return [];
  const suggestions = (error.data as { notFound?: { suggestions?: unknown } })
    ?.notFound?.suggestions;
  if (!Array.isArray(suggestions)) return [];
  return suggestions.filter(
    (s): s is ErrorPageDestination =>
      typeof s?.path === "string" &&
      s.path.startsWith("/") &&
      !s.path.startsWith("//") &&
      typeof s?.title === "string",
  );
}

/** クエリには合言葉やメールが載ることがあるので、住所の部分だけ送る */
export function referrerWithoutQuery(referrer: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    return `${url.origin}${url.pathname}`;
  } catch {
    return undefined;
  }
}

export function ErrorPageHeader({ surface }: { surface: ErrorPageSurface }) {
  return (
    <Suspense fallback={<HeaderPlaceholder surface={surface} />}>
      {surface === "lab" ? <LabErrorHeader /> : <LearnErrorHeader />}
    </Suspense>
  );
}

/*
ゆめさくエンジニアの受講生か。root の loader が帯の「ダッシュボード」の入口のために決めた値
(受講の権利の取り消しと体験の期限切れを除く) をそのまま使う。root の loader が落ちたときは false
*/
function useIsLuagateStudent(): boolean {
  try {
    const rootData = useRouteLoaderData("root") as { luagateEntry?: boolean } | undefined;
    return rootData?.luagateEntry === true;
  } catch {
    // data router の外 (試験の MemoryRouter など)。エラー画面は投げずに公開の 404 へ落とす
    return false;
  }
}

export function NotFoundScreen({ error }: { error: unknown }) {
  const { pathname } = useLocation();
  const surface = errorPageSurface(pathname);
  const student = useIsLuagateStudent() && surface === "learn";
  const destinations = notFoundDestinations(pathname);
  // 下に並べているものを「もしかして」に重ねて出さない
  const shown = [...destinations, ...LAB_ARTICLE_DESTINATIONS];
  const suggestions = notFoundSuggestionsFrom(error).filter(
    (s) => !shown.some((d) => d.path === s.path),
  );
  const suggestionCount = suggestions.length;

  useEffect(() => {
    capturePostHogEvent("page_not_found", {
      path: pathname,
      referrer: referrerWithoutQuery(document.referrer),
      surface,
      suggestion_count: suggestionCount,
      student,
    });
  }, [pathname, surface, suggestionCount, student]);

  if (student) {
    return (
      <Suspense fallback={<HeaderPlaceholder surface={surface} />}>
        <StudentNotFound header={<ErrorPageHeader surface={surface} />} />
      </Suspense>
    );
  }

  return (
    <NotFoundPage
      LinkComponent={Link}
      header={<ErrorPageHeader surface={surface} />}
      lead={SURFACE_LEAD[surface]}
      suggestions={suggestions}
      destinations={destinations}
      articles={LAB_ARTICLE_DESTINATIONS}
      artSrc={NOT_FOUND_ART_SRC}
    />
  );
}

export function ServerErrorScreen({
  status,
  errorRef,
}: {
  status: number;
  errorRef: string | null;
}) {
  const { pathname } = useLocation();
  return (
    <ServerErrorPage
      LinkComponent={Link}
      header={<ErrorPageHeader surface={errorPageSurface(pathname)} />}
      status={status}
      errorRef={status >= 500 ? errorRef : null}
      destinations={serverErrorDestinations(pathname)}
      articles={LAB_ARTICLE_DESTINATIONS}
      artSrc={SERVER_ERROR_ART_SRC}
    />
  );
}
